import React, { useState, useEffect, useCallback } from 'react';
import { paymentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Search, Printer, TrendingUp, Trash2, Edit2, X, Save, FileText, Download } from 'lucide-react';
import BillPrintModal from '../components/common/BillPrintModal';
import PaymentMethodSelect from '../components/common/PaymentMethodSelect';
import useDarkReady from '../theme/useDarkReady';
import { paidByLabel } from '../utils/paidByLabel';
import { useCompanyIdentity } from '../hooks/useCompanyIdentity';
import Num from '../components/bits/Num';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

export function EditPaymentModal({ payment, onClose, onSaved }) {
  const [form, setForm] = useState({
    receiptNumber: payment.receiptNumber || '',
    paymentMethod: payment.paymentMethod || 'cash',
    transactionId: payment.transactionId || '',
    notes: payment.notes || '',
    paymentDate: payment.paymentDate ? format(new Date(payment.paymentDate), "yyyy-MM-dd'T'HH:mm") : '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await paymentAPI.update(payment._id, form);
      toast.success('Payment updated');
      onSaved(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Payment — {payment.receiptNumber}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            <div style={{ background: 'var(--cb-f8fafc)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '.83rem', color: 'var(--text-muted)' }}>
              <strong style={{ color: 'var(--text)' }}>{payment.customer?.name}</strong>
              <span style={{ marginLeft: 8 }}>₹{fmt(payment.totalAmount)}</span>
              <span style={{ marginLeft: 8, textTransform: 'capitalize' }}>{payment.paymentFor?.replace(/_/g, ' ')}</span>
            </div>

            <div className="form-group">
              <label className="form-label">Bill / Receipt Number</label>
              <input className="form-control font-mono" value={form.receiptNumber}
                onChange={e => setForm(f => ({ ...f, receiptNumber: e.target.value }))}
                placeholder="e.g. 2026-27/145" />
            </div>

            <div className="form-group">
              <label className="form-label">Payment Method</label>
              <PaymentMethodSelect className="form-control" value={form.paymentMethod}
                onChange={v => setForm(f => ({ ...f, paymentMethod: v }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Transaction / Reference ID</label>
              <input className="form-control font-mono" value={form.transactionId}
                onChange={e => setForm(f => ({ ...f, transactionId: e.target.value }))}
                placeholder="UTR / cheque no. / ref" />
            </div>

            <div className="form-group">
              <label className="form-label">Payment Date & Time</label>
              <input type="datetime-local" className="form-control" value={form.paymentDate}
                onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PaymentsPage() {
  const { name: companyName } = useCompanyIdentity('catv');
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canEditPayment = hasModuleRight('payments', 'edit');
  const canDeletePayment = hasModuleRight('payments', 'delete');
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ page: 1, method: '', status: '', startDate: '', endDate: '', search: '', serviceType: '' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingPayment, setEditingPayment] = useState(null);
  const [billPayment, setBillPayment] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const params = { ...filters, limit: 25 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const [pd, sd] = await Promise.all([
        paymentAPI.getAll(params),
        paymentAPI.getSummary()
      ]);
      setPayments(pd.data.data);
      setPagination(pd.data.pagination);
      setSummary(sd.data.data);
    } catch { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === payments.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(payments.map(p => p._id)));
  };

  const handleDelete = async (id, receipt) => {
    if (!window.confirm(`Delete payment ${receipt}? This also removes the linked renewal record.`)) return;
    try {
      await paymentAPI.delete(id);
      toast.success('Payment deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete payment');
    }
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (!window.confirm(`Delete ${ids.length} selected payment(s)? Their linked renewal records will also be removed.`)) return;
    try {
      await paymentAPI.deleteMany(ids);
      toast.success(`${ids.length} payment(s) deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete payments');
    }
  };

  const handleSaved = (updated) => {
    setPayments(prev => prev.map(p => p._id === updated._id ? updated : p));
    setEditingPayment(null);
  };

  // CableTV-only Excel export (bix42-format comparison sheet) — reuses the same
  // /payments/export endpoint the Dashboard's "Today's Collection" export uses, driven
  // by whatever date range is currently set in the filter bar above.
  const handleExport = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error('Pick a From and To date above first');
      return;
    }
    setExporting(true);
    try {
      const { data } = await paymentAPI.exportCollections({ startDate: filters.startDate, endDate: filters.endDate });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cabletv-collections-${filters.startDate}-to-${filters.endDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const allSelected = payments.length > 0 && selectedIds.size === payments.length;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Payments</h2>
          <p className="text-muted text-small">All payment transactions</p>
        </div>
        {canDeletePayment && selectedIds.size > 0 && (
          <button className="btn btn-danger" onClick={handleDeleteSelected}>
            <Trash2 size={14} /> Delete Selected ({selectedIds.size})
          </button>
        )}
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="stats-grid pay-stats mb-6" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--cb-d1fae5)' }}>
              <TrendingUp size={20} color="var(--c-059669)" />
            </div>
            <div className="stat-info">
              <p>Today's Collection</p>
              <h3><Num value={summary.today?.amount || 0} prefix="₹" /></h3>
              <div className="trend">{summary.today?.count} transactions</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--cb-dbeafe)' }}>
              <TrendingUp size={20} color="var(--primary)" />
            </div>
            <div className="stat-info">
              <p>This Month</p>
              <h3><Num value={summary.month?.amount || 0} prefix="₹" /></h3>
              <div className="trend">{summary.month?.count} payments</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--cb-fee2e2)' }}>
              <TrendingUp size={20} color="var(--c-dc2626)" />
            </div>
            <div className="stat-info">
              <p>Pending</p>
              <h3><Num value={summary.pending} /></h3>
              <div className="trend">awaiting collection</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search customer, mobile, receipt..."
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <input type="date" className="form-control" style={{ width: 150 }}
          value={filters.startDate} onChange={e => setFilters(f => ({ ...f, startDate: e.target.value, page: 1 }))} />
        <span style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>to</span>
        <input type="date" className="form-control" style={{ width: 150 }}
          value={filters.endDate} onChange={e => setFilters(f => ({ ...f, endDate: e.target.value, page: 1 }))} />
        <select className="form-control" style={{ width: 140 }}
          value={filters.serviceType} onChange={e => setFilters(f => ({ ...f, serviceType: e.target.value, page: 1 }))}>
          <option value="">CableTV & Broadband</option>
          <option value="catv">CableTV</option>
          <option value="broadband">Broadband</option>
        </select>
        <select className="form-control" style={{ width: 140 }}
          value={filters.method} onChange={e => setFilters(f => ({ ...f, method: e.target.value, page: 1 }))}>
          <option value="">All Methods</option>
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="cheque">Cheque</option>
          <option value="payment_gateway">Payment Gateway</option>
          <option value="adjustment">Adjustment</option>
          <option value="coupon">Coupon</option>
        </select>
        <button className="btn btn-secondary" onClick={() => setFilters({ page: 1, method: '', status: '', startDate: '', endDate: '', search: '', serviceType: '' })}>
          Clear
        </button>
        <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}
          title="Excel export of CableTV collections in the selected date range (bix42-comparison format)">
          <Download size={14} /> {exporting ? 'Exporting…' : 'Export Excel'}
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  {canDeletePayment && (
                    <th style={{ width: 36 }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                    </th>
                  )}
                  <th>Receipt</th><th>Date</th><th>Customer</th><th>For</th><th>Method</th><th>Txn ID</th><th>Amount</th><th>By</th><th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p._id} style={selectedIds.has(p._id) ? { background: 'var(--primary-light, #eff6ff)' } : {}}>
                    {canDeletePayment && (
                      <td>
                        <input type="checkbox" checked={selectedIds.has(p._id)} onChange={() => toggleSelect(p._id)} />
                      </td>
                    )}
                    <td className="font-mono" style={{ fontSize: '.78rem' }}>{p.receiptNumber}</td>
                    <td data-keep style={{ fontSize: '.82rem' }}>{format(new Date(p.paymentDate), 'dd/MM/yy HH:mm')}</td>
                    <td data-title>
                      <div style={{ fontWeight: 500 }}>{p.customer?.name}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{p.customer?.customerId}</div>
                    </td>
                    <td style={{ fontSize: '.8rem', textTransform: 'capitalize' }}>{p.paymentFor?.replace(/_/g, ' ')}</td>
                    <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{p.paymentMethod === 'company_paid' ? paidByLabel(companyName) : p.paymentMethod?.replace(/_/g, ' ')}</span></td>
                    <td className="font-mono" style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{p.transactionId || '—'}</td>
                    <td data-keep style={{ fontWeight: 700, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</td>
                    <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{p.collectedBy?.name}</td>
                    <td>
                      <div className="pay-actions" style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" title="Generate Bill" onClick={() => setBillPayment(p)}>
                          <FileText size={12} />
                        </button>
                        <a href={`/api/v1/payments/${p._id}/receipt`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" title="PDF Receipt">
                          <Printer size={12} />
                        </a>
                        {canEditPayment && (
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditingPayment(p)}>
                            <Edit2 size={12} />
                          </button>
                        )}
                        {canDeletePayment && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p._id, p.receiptNumber)}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginRight: 8 }}>
              Page {pagination.page} of {pagination.pages} · {pagination.total} total
            </span>
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      {editingPayment && (
        <EditPaymentModal
          payment={editingPayment}
          onClose={() => setEditingPayment(null)}
          onSaved={handleSaved}
        />
      )}

      {billPayment && (
        <BillPrintModal
          payment={billPayment}
          onClose={() => setBillPayment(null)}
        />
      )}
    </div>
  );
}
